import { isNavigationAbortMessage } from './navigation-abort';

/**
 * A full document navigation cancels every request the old page still has in
 * flight. React Router turns a cancelled `.data` request into a route error, so
 * the old page swaps to an error page for the moment it has left before the new
 * document commits. Firefox makes this visible: its 500ms post-login wait (see
 * `handleAuthRedirect`) sits exactly where the home page's post-login fetchers
 * are still loading.
 *
 * Code that replaces the document marks the navigation as pending first. While
 * it is pending, `withDocumentNavigationAbortHold` holds back the rejection of a
 * cancelled `.data` request, so React Router never sees it and the old page stays
 * on screen until the new one replaces it.
 *
 * Only navigations started through `assignDocumentLocation` / `reloadDocument`
 * are covered. A user typing a URL is not, because detecting that needs a
 * `beforeunload` listener, and Firefox refuses the back/forward cache to any page
 * that registers one.
 */

/**
 * How long a marked navigation may take to commit before cancelled requests are
 * released again. Long enough for a slow connection to fetch the next document;
 * short enough that a navigation which never happens still ends on the
 * self-healing `<NetworkError />` page rather than a page that silently stopped
 * loading.
 */
export const DOCUMENT_NAVIGATION_GRACE_MS = 10_000;

let pendingSince: number | null = null;
let clearsOnRestore = false;

export const clearDocumentNavigationPending = (): void => {
    pendingSince = null;
};

/** A page restored from the back/forward cache is live again, not leaving. */
const clearOnRestore = (event: PageTransitionEvent) => {
    if (event.persisted) clearDocumentNavigationPending();
};

export const markDocumentNavigationPending = (
    now: number = Date.now(),
): void => {
    pendingSince = now;
    if (clearsOnRestore || typeof window === 'undefined') return;
    // `pageshow`, unlike `beforeunload`, leaves the page eligible for bfcache.
    window.addEventListener('pageshow', clearOnRestore);
    clearsOnRestore = true;
};

/** Milliseconds left in the grace window; 0 when no navigation is pending. */
export const documentNavigationGraceRemaining = (
    now: number = Date.now(),
): number => {
    if (pendingSince === null) return 0;
    return Math.max(0, pendingSince + DOCUMENT_NAVIGATION_GRACE_MS - now);
};

/** `window.location.href = url`, marked so cancelled requests don't flash an error page. */
export const assignDocumentLocation = (url: string): void => {
    markDocumentNavigationPending();
    window.location.href = url;
};

/** `window.location.reload()`, marked so cancelled requests don't flash an error page. */
export const reloadDocument = (): void => {
    markDocumentNavigationPending();
    window.location.reload();
};

const requestUrl = (input: RequestInfo | URL): string => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    return input.url;
};

/** A same-origin React Router single-fetch request (`/_root.data`, `/remix-api/x.data`). */
export const isSameOriginDataRequest = (input: RequestInfo | URL): boolean => {
    try {
        const url = new URL(requestUrl(input), window.location.href);
        return (
            url.origin === window.location.origin &&
            url.pathname.endsWith('.data')
        );
    } catch {
        return false;
    }
};

/**
 * Wrap `fetch` so a `.data` request cancelled by a marked document navigation
 * rejects only after the grace window, if at all. React Router calls the global
 * `fetch` for every loader and fetcher, so wrapping it once in `entry.client.tsx`
 * covers them all. Everything else — other URLs, other failures, and any
 * failure with no navigation pending — passes through untouched.
 */
export const withDocumentNavigationAbortHold =
    (fetchImpl: typeof fetch): typeof fetch =>
    (input, init) =>
        fetchImpl(input, init).catch((error: unknown) => {
            const wait = documentNavigationGraceRemaining();
            const isCancelledDataRequest =
                error instanceof Error &&
                isNavigationAbortMessage(error.message) &&
                isSameOriginDataRequest(input);
            if (wait === 0 || !isCancelledDataRequest) throw error;
            return new Promise<Response>((_resolve, reject) => {
                setTimeout(() => reject(error), wait);
            });
        });
